import { Injectable, Injector, NgZone } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { MCRootStoreService, MCRootDataService, MCDialogData, MCConfigService } from '@maslul/common-services';
import { Observable, throwError, EMPTY } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class Interceptor implements HttpInterceptor {

  constructor(private injector: Injector) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (request.url.includes(environment.configFile)) {
      return next.handle(request);
    }

    const configService = this.injector.get(MCConfigService);
    try {
      configService.getConfiguration();
    } catch (e) {
      return next.handle(request);
    }

    const rootStore = this.injector.get(MCRootStoreService);
    const token = rootStore.getSnapshot().token;

    if (token != null) {
      request = request.clone({
        headers: request.headers.set('Authorization', 'Bearer ' + token)
      });
    }

    return next.handle(request).pipe(
      map((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
          const jwtToken = event.headers.get('TokenJWT');
          if (jwtToken) {
            rootStore.setToken(jwtToken);
          }
        }
        return event;
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 404) {
          rootStore.setIsShowSpinner('Interceptor catch error', false, false);
          return this.navigateToNotFound404(error);
        }

        else if (error.status === 401 || error.status === 403) {
          if (error.error?.indexOf && error.error.indexOf('expired') > -1) {
            const rootDataService = this.injector.get(MCRootDataService);

            return rootDataService.refreshToken().pipe(
              switchMap(() => {
                const newToken = rootStore.getSnapshot().token;
                request = request.clone({
                  headers: request.headers.set('Authorization', 'Bearer ' + newToken)
                });
                return next.handle(request).pipe(
                  catchError((innerError: any) => {
                    if (innerError.status === 401 || innerError.status === 403) {
                      rootStore.setIsShowSpinner('Interceptor catch error', false, false);
                      return this.navigateToUnauthorized(innerError);
                    }
                    return throwError(() => innerError);
                  })
                );
              })
            );
          } else {
            rootStore.setIsShowSpinner('Interceptor catch error', false, false);
            return this.navigateToUnauthorized(error);
          }
        }

        else if (error.status === 302 || error.status === 0 ||
          (error.statusText && error.statusText.toLowerCase().indexOf("unknown error") > -1) ||
          (error.name && error.name.toLowerCase().indexOf("httperrorresponse") > -1 && error.status === 200)) {

          rootStore.setDialogData({
            dialogBody: "<div style='text-align:center;'>הקשר למערכת מסלול נותק<br />בהתחברות עם כרטיס חכם יש לסגור קודם את הדפדפן</div>",
            showDialog: true,
            dialogHeader: "שגיאת הזדהות",
            eventOnHide: (e: any) => { e.exit(); }
          });
          return EMPTY;
        }

        else if (error.status === 409) {
          if (error.error?.indexOf && error.error.indexOf('refresh') > -1) {
            rootStore.setDialogData({
              dialogBody: "<div style='text-align:center;'>הנתונים אינם מעודכנים, יש לרענן את המסך</div>",
              showDialog: true,
              dialogHeader: "שגיאת עדכון",
              eventOnHide: (e: any) => { e.refresh(); }
            });
            return EMPTY;
          }
          return throwError(() => error);
        }

        else {
          rootStore.setIsShowSpinner('Interceptor catch error', false, false);

          if (error.error && error.error.code === undefined) {
            rootStore.setDialogData({
              dialogBody: "<div style='text-align:center;'>חלה שגיאה במערכת מסלול - אנא נסה שנית לאחר רענון המסך</div>",
              showDialog: true,
              dialogHeader: "שגיאת מערכת",
              eventOnHide: (e: any) => {
                rootStore.setDialogData(new MCDialogData());
              }
            });
            return EMPTY;
          }
          return throwError(() => error.error || error);
        }
      })
    );
  }

  private navigateSafe(path: string) {
    const router = this.injector.get(Router);
    const ngZone = this.injector.get(NgZone);
    ngZone.run(() => {
      router.navigate([path]);
    });
    return EMPTY;
  }

  navigateToUnauthorized(error: any) {
    return this.navigateSafe('unauthorized');
  }

  navigateToNotFound404(error: any) {
    return this.navigateSafe('not-found');
  }
}